import { type App, computed, type ComputedRef, type Ref, ref, watch } from 'vue'
import { type AppTranslator } from '@/i18n'

import {
    type AutologinMutation,
    type AutologinMutationVariables,
    type InitQuery,
    type InitQueryVariables,
} from '@/graphql/api'
import { type MenuPlugin } from '@/plugins/menu.ts'
import { trackGtmEvent } from '@/composables/app/gtm/event.ts'
import { type AuthPlugin } from '@/plugins/auth.ts'
import { type UseMutationResponse, type UseQueryResponse } from '@urql/vue'
import { initKey, type InitPlugin } from '@/plugins/init/use-init.ts'
import { type BenchFunction } from '@/plugins/bench.ts'
import type { CountryPlugin } from '@/plugins/country.ts'

interface InitDependencies {
    translator: AppTranslator
    countryPlugin: CountryPlugin
    menus: MenuPlugin
    auth: AuthPlugin
    isAdmin: ComputedRef<boolean>
    authToken: string | null
    bench: BenchFunction
    initQuery: UseQueryResponse<InitQuery, InitQueryVariables | undefined>
    pauseInitQuery: Ref<boolean>
    autoLoginMutation: UseMutationResponse<
        AutologinMutation,
        AutologinMutationVariables | undefined
    >
}

export function installInit(app: App, plugin: InitPlugin): void {
    app.provide(initKey, plugin)
}

export function createInit(dependencies: InitDependencies): InitPlugin {
    const {
        translator,
        menus,
        auth,
        isAdmin,
        authToken,
        bench,
        autoLoginMutation,
        initQuery,
        pauseInitQuery,
    } = dependencies

    const { setMessages } = translator
    const {
        setTopBar,
        setPreHeader,
        setMainMenu,
        setFooter,
        setPaymentIcons,
        setSocialNetworks,
    } = menus
    const { setUser } = auth

    const benchmark = bench('Query init')

    const { data, error } = initQuery

    async function processInitData(data: InitQuery): Promise<void> {
        // processTranslations(data.language?.translations || [], translator, ref(region))
        setTopBar(data.topBar)
        setPreHeader(data.preHeader)
        setMainMenu(data.mainMenu)
        setFooter(data.footer)
        setPaymentIcons(data.paymentIcons)
        setSocialNetworks(data.socialNetworks)
        setUser(data.me)

        if (data.language) {
            // За бъдещите поколения
            // ДА НЕ СЕ МЕСТИ НАД РИНГ БИЛДЪРА
            // ЧЕ СТАВА МНОГО ЛОШО
            // ЗА ОСТАНАЛИТЕ, НЕ Е СЪВСЕМ СИГУРНО
            await setMessages(data.language.locale, data.language.translations)
        }
    }

    const isInitialized = ref(false)

    trackGtmEvent('site_version', { 'version': 'v2' })

    const waitForInit = async (): Promise<void> => {
        if (authToken) {
            const autoLoginBench = bench('Mutation AutoLogin')
            const response = await autoLoginMutation.executeMutation({
                authToken: authToken,
                isConsultant: isAdmin.value,
            })
            autoLoginBench.end('END')

            if (
                response.data?.autologin.__typename === 'LoginResponseSuccess'
            ) {
                setUser(response.data?.autologin.user)
            }

            // TODO remove logic from RegionModal and uncomment this
            // after teleport/suspense/navigation bug is fixed
            // https://github.com/vuejs/core/issues/7966
            // authToken.value = null

            if (QA || import.meta.env.SSR) {
                benchmark.restart()
            }
        }

        pauseInitQuery.value = false

        await initQuery
        if (QA || import.meta.env.SSR) {
            benchmark.end('END')
        }

        if (data.value) {
            await processInitData(data.value)
        }

        isInitialized.value = true

        watch(data, (data) => {
            if (!data) {
                return
            }
            // This watcher will set VAT/Rates a second time during INIT in a browser
            // Nothing we can do, but we NEED to set VAT/Rates in the promises
            // Due to the fact that SSR will not react to this
            return processInitData(data)
        })

        if (QA || import.meta.env.SSR) {
            benchmark.end('InitQuery Data Processed')
        }
    }

    const promise = waitForInit()

    return {
        error,
        initQueryResponse: initQuery,
        isInitialized,
        promise,
        benchmark,
        enquiryFormContent: computed(() => {
            return Object.fromEntries(
                data.value?.enquiryFormContents.map((content) => {
                    return [content.slug, content]
                }) ?? []
            )
        }),
    }
}
